import { Grid } from "@material-ui/core";
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useParams } from "react-router-dom";
import { isRequestingRole } from "../../../../redux/actions/userAction";
import VerticalLinearStepper from "../../../VerticalLinearStepper/VerticalLinearStepper";
import useFeatureFlags from "../../../shared/services/featureToggle/useFeatureFlags";
import withFeatureFlagUpdates from "../../../shared/services/featureToggle/withFeatureFlagUpdates";

// Import constants
import { REQUEST_TYPES } from "../../../shared/constants/RequestTypes";
import { FM_STATUSES } from "../../../shared/constants/FMStatuses";
import { CPM_STATUS_REVIEW_REQUEST } from "../../../shared/constants/FeatureToggleKeys";

// Import page components
import AttachmentsPage from "./Pages/AttachmentsPage";
import RequestDetailsPage from "./Pages/RequestDetailsPage";
import RequestTypeSelection from "./Pages/RequestTypeSelection";
import ReviewAndSubmitPage from "./Pages/ReviewAndSubmitPage";
import SuccessPage from "./Pages/SuccessPage";

// Import shared components
import ProjectInfoBanner from "../../../SharedComponents/ProjectInfoBanner/ProjectInfoBanner";

// Import CSS
import "../../../AddNewProject/AddNewProject.css";
import "./CreateStatusReviewRequest.css";

const CreateStatusReviewRequest = () => {
  const history = useHistory();

  const { isFeatureEnabled, flagUpdateCounter } = useFeatureFlags([CPM_STATUS_REVIEW_REQUEST]);  

  // Check permissions
  if (!isRequestingRole() || !isFeatureEnabled(CPM_STATUS_REVIEW_REQUEST)) {
    history.replace("/unauthorized");
    return null;
  }

  const { project_id: projectId } = useParams();
  const dispatch = useDispatch();

  const [stepCount, setStepCount] = useState(0);
  const [projectInfo, setProjectInfo] = useState(null);
  const [formState, setFormState] = useState({});
  const [requestCallback, setRequestCallback] = useState();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [availableRequestTypes, setAvailableRequestTypes] = useState([]);
  
  const requiredDocuments = useSelector(store => store.lenderRequestReducer.requiredDocTypesWithCount);
  const optionalDocuments = useSelector(store => store.lenderRequestReducer.optionalDocTypes);

  const steps = useMemo(() => [
    "Select Request Type",
    "Request Details",
    "Attach Documents",
    "Review & Submit",
    "Confirmation"
  ], []);

  // Fetch project information on component mount
  useEffect(() => {    
    if (projectId) {
      fetchProjectInfo();
    }
  }, [projectId]);

  useEffect(() => {
    if (projectInfo) {
      const availableRequestTypes = getAvailableRequestTypes(projectInfo?.fnmaReviewStatus);
      setAvailableRequestTypes(availableRequestTypes);
    }
  }, [projectInfo]);

  async function fetchProjectInfo() {
    try {
      setLoading(true);

      const { data: project } = await axios.get(
        global.REACT_APP_API_ENDPOINT +
        `/condoMgr/getProjectDetailsByProjectId/` +
        projectId
      );

      if (!project || project === `Cannot find project:${projectId}`) {
        console.warn(project);
        return;
      }

      setProjectInfo(project.projectProfile);
      setError(null);
    } catch (err) {
      console.error("Error fetching project info:", err);
      setError("Failed to load project information");
    } finally {
      setLoading(false);
    }
  };

  // Determine available request types based on current FM Review Status
  const getAvailableRequestTypes = (currentStatus) => {
    return currentStatus === FM_STATUSES.UNAVAILABLE
      ? [REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS, REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS]
      : [REQUEST_TYPES.ADD_UNAVAILABLE_STATUS];
  };

  const decrementCounter = (formStateData) => {
    if (formStateData) {
      setFormState(formStateData);
    }
    
    let newStep = stepCount - 1;

    if (stepCount === 3 && !requiredDocuments?.length) {
      newStep--;
    }

    setStepCount(newStep);
  };

  const incrementCounter = (formStateData, requestCallbackFn) => {
    if (requestCallbackFn) {
      setRequestCallback({ callBack: requestCallbackFn });
    }
    if (formStateData) {
      setFormState(formStateData);
    }

    setStepCount(stepCount + 1);
  };

  const renderCurrentStep = () => {
    const commonProps = {
      projectInfo,
      formState,
      incrementCounter,
      decrementCounter,
      projectId
    };

    switch (stepCount) {
      case 0:
        return (
          <RequestTypeSelection
            {...commonProps}
            availableRequestTypes={availableRequestTypes}
          />
        );
      case 1:
        return <RequestDetailsPage {...commonProps} />;
      case 2:
        return (
          <AttachmentsPage 
            {...commonProps} 
            requirements={requiredDocuments} 
            optional={optionalDocuments} 
          />
        );
      case 3:
        return <ReviewAndSubmitPage {...commonProps} />;
      case 4:
        return (
          <SuccessPage
            {...commonProps}
            requestCallback={requestCallback}
          />
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Grid container>
        <Grid item xs={3} style={{ backgroundColor: "#F2F2F2" }}>
          <VerticalLinearStepper
            steps={steps}
            activeStep={stepCount}
          />
        </Grid>
        <Grid
          container
          item
          xs={9}
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          style={{ padding: "20px", border: "solid 1px black" }}
        >
          <div>Loading project information...</div>
        </Grid>
      </Grid>
    );
  }

  if (error) {
    return (
      <Grid container>
        <Grid item xs={3} style={{ backgroundColor: "#F2F2F2" }}>
          <VerticalLinearStepper
            steps={steps}
            activeStep={stepCount}
          />
        </Grid>
        <Grid
          container
          item
          xs={9}
          direction="row"
          justifyContent="flex-start"
          alignItems="flex-start"
          style={{ padding: "20px", border: "solid 1px black" }}
        >
          <div>
            <h3>Error</h3>
            <p>{error}</p>
            <button onClick={() => history.goBack()}>Go Back</button>
          </div>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid container className="create-request-container">
      <Grid item xs={3} style={{ backgroundColor: "#F2F2F2" }}>
        <VerticalLinearStepper
          steps={steps}
          activeStep={stepCount}
        />
      </Grid>
      <Grid
        container
        item
        xs={9}
        direction="row"
        justifyContent="flex-start"
        alignItems="flex-start"
        style={{ padding: "20px", border: "solid 1px black" }}
      >
        <ProjectInfoBanner projectId={projectId} projectName={projectInfo?.projectName} />
        {renderCurrentStep()}
      </Grid>
    </Grid>
  );
};

export default withFeatureFlagUpdates(CreateStatusReviewRequest, [CPM_STATUS_REVIEW_REQUEST]);
